// @ts-check

/**
 * @var window.LeaderLine
 */

/**
 * @typedef {Object} HotspotRelation
 * @property {string} relation - The unique identifier of the related hotspot.
 * @property {string} relationship_description - The description of the relationship between the hotspots.
 *
 * @typedef {Object} HotspotItem
 * @property {HTMLElement} el - The HTML element representing the storytelling item.
 * @property {Object} data - The data associated with this storytelling item.
 * @property {string} data.uid - The unique identifier for this item.
 * @property {string} data.headshot_url - The URL of the headshot image for this item.
 * @property {string} data.name - The name of the person or entity represented by this item.
 * @property {string} data.title - The title of the person or entity represented by this item.
 * @property {string} data.description - A description of the person or entity represented by this item.
 * @property {string} data.color - The hex color code associated with this item.
 * @property {Array<HotspotRelation>} data.relationship - The relationships this item has with other items.
 *
 * @typedef {Object.<string, HotspotItem>} HotspotsCollection
 */

const InteractiveStorytelling = ( function() {

	/**
     * Elements for the interactive storytelling.
     */
    let container,
		containerItems,
        flyout,
        flyoutCloseButton,
		flyoutBackButton,
        flyoutHeadshot,
        flyoutName,
		flyoutTitle,
        flyoutDescription,
        flyoutRelationship,
		flyoutRelationshipContainer,
		flyoutRelationshipWrapper;

	/**
	 * @type {HotspotsCollection} The collection of hotspot elements.
	 */
	let hotspots = {};

	/**
	 * @type {HotspotItem|null} The currently selected hotspot.
	 */
    let selectedHotspot = null;

	/**
	 * @type {HotspotRelation|null} The currently selected hotspot relation.
	 */
	let selectedHotspotRelation = null;

	/**
	 * @type {LeaderLine[]} The collection of relationship lines.
	 */
	let relationshipLines = [];

	/**
	 * @type {number} The breakpoint for the flyout.
	 */
	const breakpoint = 800;

	/**
	 * Populate the flyout with the data from the selected hotspot.
	 *
	 * @param {HotspotItem} item
	 */
	function populateFlyout( item ) {
		const itemData = item.data;

		const updating = ! selectedHotspot || selectedHotspot.data.uid !== itemData.uid

		flyout.style.setProperty( '--item-color', item.data.color );

		let headshot = itemData.headshot_url;
		let name = itemData.name;
		let title = itemData.title;
		let description = itemData.description;

		// If we are focusing in on a relationship.
		if ( null !== selectedHotspotRelation ) {
			flyout.classList.add( 'focused-relation' );

			const relation = hotspots[ selectedHotspotRelation.relation ];

			if ( relation ) {
				headshot = relation.data.headshot_url;
				name = relation.data.name;
				title = relation.data.title;
				description = selectedHotspotRelation.relationship_description;
			}
		} else {
			flyout.classList.remove( 'focused-relation' );
		}

		flyoutHeadshot.src = headshot;
		flyoutName.textContent = name;
		flyoutTitle.textContent = title;
		flyoutDescription.innerHTML = description;

		// If we are changing the selected hotspot...
		if ( updating ) {

			// Update related hotspots.
			const relatedHotspots = itemData.relationship
				.map( relationObj => hotspots[ relationObj.relation ] )
				.filter( hotspot => hotspot !== undefined && hotspot !== null );

			drawFlyoutRelatedHotspots( item, relatedHotspots );
		}

		// Focus the flyout.
		flyout.focus();
	}

	/**
	 * Draw the related hotspots in the flyout.
	 *
	 * @param {HotspotItem} originalHotspot
	 * @param {HotspotItem[]} relatedHotspots
	 */
	function drawFlyoutRelatedHotspots( originalHotspot, relatedHotspots ) {
		// Clear the existing related hotspots.
		flyoutRelationship.innerHTML = '';

		// If no related hotspots, hide the relationships section.
		if ( 0 === relatedHotspots.length ) {
			flyoutRelationshipContainer.classList.add( 'hidden' );
			return;
		}

		flyoutRelationshipContainer.classList.remove( 'hidden' );

		relatedHotspots.forEach( hotspot => {
			// Render the headshot container.
			const headshotContainer = document.createElement( 'div' );
			headshotContainer.classList.add( 'interactive-storytelling__flyout-relationship-headshot' );

			// Render the headshot image.
			const headshot = document.createElement( 'img' );
			headshot.src = hotspot.data.headshot_url;
			headshot.alt = hotspot.data.name;

			headshotContainer.appendChild( headshot );

			// Render the relationship.
			const relationship = document.createElement( 'button' );
			relationship.classList.add( 'interactive-storytelling__flyout-relationship' );
			relationship.setAttribute( 'data-rel-id', originalHotspot.data.uid + '|' + hotspot.data.uid );
			relationship.appendChild( headshotContainer );
			// relationship.style.setProperty( '--item-color', hotspot.data.color );

			flyoutRelationship.appendChild( relationship );
		} );
	}

	/**
	 * Adjust related leader line style when hovering or clicking on hotspots.
	 *
	 * @param {HotspotItem} hotspot
	 */
	function highlightRelatedHotspots( hotspot ) {
		// Remove all related classes from hotspots.
		Object.values( hotspots ).forEach( hotspot => {
			hotspot.el.classList.remove( 'related' );
		} );

		// Revert all relationships lines.
		relationshipLines.forEach( line => {
			const lineEl = line.getSVGElm();

			lineEl.classList.remove( 'active' );
			line.setOptions( { size: 1 } );
		} );

		// Add the related class to each related hotspot.
		hotspot.data.relationship.forEach( relationObj => {
			const relatedHotspot = hotspots[ relationObj.relation ];

			if ( relatedHotspot ) {
				relationshipLines.forEach( line => {
					const lineEl = line.getSVGElm();

					if ( lineEl.getAttribute( 'data-hs' ) === hotspot.data.uid || lineEl.getAttribute( 'data-he' ) === hotspot.data.uid ) {
						lineEl.classList.add( 'active' );
						line.setOptions( { size: 2 } );

						// Set the related class to the hotspots.
						hotspots[ lineEl.getAttribute( 'data-hs' ) ].el.classList.add( 'related' );
						hotspots[ lineEl.getAttribute( 'data-he' ) ].el.classList.add( 'related' );

						// Set color to the same as the selected hotspot.
						hotspots[ lineEl.getAttribute( 'data-hs' ) ].el.style.setProperty( '--item-color', hotspot.data.color );
						hotspots[ lineEl.getAttribute( 'data-he' ) ].el.style.setProperty( '--item-color', hotspot.data.color );
					}
				} );
			}
		} );
	}

	/**
	 * Revert back to the original state of the hotspots and relationship lines.
	 */
	function clearHighlightedRelatedHotspots() {
		flyoutRelationship.innerHTML = '';

		Object.values( hotspots ).forEach( hotspot => {
			hotspot.el.classList.remove( 'related' );
			hotspot.el.style.setProperty( '--item-color', hotspot.data.color );
		} );

		relationshipLines.forEach( line => {
			const lineEl = line.getSVGElm();

			lineEl.classList.remove( 'active' );
			line.setOptions( { size: 1 } );
		} );
	}

	/**
	 * Set the selected hotspot and display the flyout.
	 *
	 * @param {HotspotItem} hotspot
	 * @param {boolean} clearRelation
	 */
	function setSelectedHotspot( hotspot, clearRelation = true ) {
		if ( selectedHotspot ) {
			selectedHotspot.el.classList.remove( 'active' );
		}

		if ( clearRelation ) {
			selectedHotspotRelation = null;

			flyoutBackButton.classList.remove( 'visible' );
		}

		populateFlyout( hotspot );
		highlightRelatedHotspots( hotspot );

		selectedHotspot = hotspot;

		hotspot.el.classList.add( 'active' );

		container.classList.add( 'has-flyout' );
		flyout.setAttribute( 'aria-expanded', 'true' );
		flyout.setAttribute( 'tabindex', '0' );
	}

	/**
	 * Set the selected hotspot and relation view.
	 *
	 * @param {HotspotItem} hotspot
	 * @param {HotspotRelation} relation
	 */
	function setSelectedHotspotRelation( hotspot, relation ) {
		selectedHotspotRelation = relation;

		setSelectedHotspot( hotspot, false );

		flyoutBackButton.classList.add( 'visible' );
	}

	/**
	 * Generate leader lines to the relationships this hotspot has.
	 */
	function generateRelationshipLines() {
		relationshipLines = [];

		Object.values(hotspots).forEach( ( hotspot ) => {
			const relationObj = hotspot.data.relationship;

			relationObj.forEach( ( relation ) => {
				const relatedHotspot = hotspots[ relation.relation ];

				if ( relatedHotspot ) {
					// Get leader line color from container CSS variable --relationship-line-color.
					const color = getComputedStyle( container ).getPropertyValue( '--relationship-line-color' ).trim();

					// @ts-ignore
					const line = new window.LeaderLine(
						window.LeaderLine.pointAnchor(hotspot.el, {x: '50%', y: '50%'}),
						window.LeaderLine.pointAnchor(relatedHotspot.el, {x: '50%', y: '50%'}),
						{
							color: color || 'white',
							size: 1,
							path: 'straight',

						}
					);

					line.getSVGElm().setAttribute( 'data-hs', hotspot.data.uid );
					line.getSVGElm().setAttribute( 'data-he', relation.relation );

					line.setOptions( { hs: hotspot.el, he: relatedHotspot.el } );

					containerItems.addEventListener( 'resizing', ( e ) => {
						line.position();
					} );

					relationshipLines.push( line );
				}
			} );
		} );
	}

	/**
	 * Clear the selected hotspot and flyout.
	 */
    function clearSelectedHotspot() {
		container.classList.remove( 'has-flyout' );
		flyout.setAttribute( 'aria-expanded', 'false' );
		flyout.setAttribute( 'tabindex', '-1' );

		flyout.removeAttribute( 'style' );
		flyout.classList.remove( 'focused-relation' );

		if ( ! selectedHotspot ) {
			return;
		}

		selectedHotspot.el.classList.remove( 'active' );

        selectedHotspot = null;
		selectedHotspotRelation = null;

		flyoutHeadshot.src = '';
		flyoutName.textContent = '';
		flyoutTitle.textContent = '';
		flyoutDescription.innerHTML = '';

		clearHighlightedRelatedHotspots();

		if ( window.innerWidth < breakpoint ) {
			container.scrollIntoView( { behavior: 'smooth', block: 'start' } );
		}

		flyoutBackButton.classList.remove( 'visible' );
    }

	/**
	 * Clear the selected hotspot relation.
	 */
	function clearSelectedHotspotRelation() {
		if ( ! selectedHotspot ) {
			return;
		}

		setSelectedHotspot( selectedHotspot, true );

		flyoutRelationshipWrapper.scrollLeft = 0;

		// Remove active state from the relationships.
		const activeRelationships = flyout.querySelectorAll( '.interactive-storytelling__flyout-relationship.active' );

		activeRelationships.forEach( relationship => {
			relationship.classList.remove( 'active' );
		} );
	}

	/**
	 * Event listener: Clicking on a hotspot.
	 *
	 * @param {Object} event - The event object.
	 * @param {HTMLElement} event.currentTarget - The current target element.
	 * @param {HTMLElement} event.target - The target element.
	 */
	function handleHotspotClick( event ) {
		if ( ! event.currentTarget ) {
			return false;
		}

		const target = event.currentTarget;

		if ( ! ( target instanceof HTMLElement ) ) {
			return false;
		}

		const hotspot = target.getAttribute( 'data-uid' );

		if ( ! hotspot || ! hotspots[ hotspot ] ) {
			return false;
		}

		// Clear the selected hotspot if it's the same one.
		if ( selectedHotspot && selectedHotspot.data.uid === hotspot ) {
			clearSelectedHotspot();
			return;
		}

        setSelectedHotspot( hotspots[ hotspot ] );
    }

	/**
	 * Event listener: Clicking on a related hotspot.
	 *
	 * @param {Object} event - The event object.
	 * @param {HTMLElement} event.currentTarget - The current target element.
	 * @param {HTMLElement} event.target - The target element.
	 */
	function handleHotspotRelationClick( event ) {
		if ( ! event.currentTarget ) {
			return false;
		}

		const target = event.currentTarget;

		if ( ! ( target instanceof HTMLElement ) ) {
			return false;
		}

		const hotspotRel = target.getAttribute( 'data-rel-id' );

		if ( ! hotspotRel ) {
			return false;
		}

		// Split by pipe.
		const [ hotspot, relation ] = hotspotRel.split( '|' );

		if ( ! hotspot || ! hotspots[ hotspot ] ) {
			return false;
		}

		/**
		 * @type {HotspotRelation|undefined}
		 */
		const hotspotRelation = hotspots[ hotspot ].data.relationship.find( rel => rel.relation === relation );

		// Check if hotspot has the relation.
		if ( ! hotspotRelation ) {
			return false;
		}

		setSelectedHotspotRelation( hotspots[ hotspot ], hotspotRelation );

		// Clear other active relationships.
		const activeRelationships = flyout.querySelectorAll( '.interactive-storytelling__flyout-relationship.active' );

		activeRelationships.forEach( relationship => {
			relationship.classList.remove( 'active' );
		} );

		// Set class to the relationship.
		target.classList.add( 'active' );

		if ( window.innerWidth < breakpoint ) {
			flyout.scrollIntoView( { behavior: 'smooth', block: 'start' } );
		}

		flyoutRelationshipWrapper.scrollLeft = target.offsetLeft - flyoutRelationshipWrapper.offsetWidth / 2 + target.offsetWidth / 2;
	}

	/**
	 * Setup the hotspots for the interactive storytelling.
	 */
	function setupHotspots() {
		let _hotspots = container.querySelectorAll( '.interactive-storytelling__item' );

		_hotspots.forEach( hotspot => {
			const uid = hotspot.getAttribute( 'data-uid' );
			const _hotspot = {};

			_hotspot.el = hotspot;

			try {
				_hotspot.data = JSON.parse( hotspot.getAttribute( 'data-item' ) );
				_hotspot.data.color = '' === _hotspot.data.color ? getComputedStyle( hotspot ).getPropertyValue( '--item-color' ).trim() : _hotspot.data.color;

				hotspots[ uid ] = _hotspot;
			} catch (error) {
				console.warn( 'Interactive Storytelling:', error );
			}
		} );

		return hotspots;
	}

	/**
	 * Setup the variables for the interactive storytelling.
	 */
	function setupVariables() {
		containerItems 				= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__items' ) );
		flyout 						= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout' ) );
		flyoutCloseButton 			= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-close' ) );
		flyoutBackButton 			= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-back' ) );
		flyoutHeadshot 				= /** @type {HTMLImageElement} */ ( container.querySelector( '.interactive-storytelling__flyout-headshot img' ) );
		flyoutName 					= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-name' ) );
		flyoutTitle 				= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-title' ) );
		flyoutDescription 			= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-description' ) );
		flyoutRelationship 			= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-relationships' ) );
		flyoutRelationshipContainer = /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-relationships-container' ) );
		flyoutRelationshipWrapper 	= /** @type {HTMLElement} */ ( container.querySelector( '.interactive-storytelling__flyout-relationships-wrapper' ) );
	}

	/**
	 * Setup the event listeners for the interactive storytelling.
	 */
	function setupEvents() {
		flyoutCloseButton.addEventListener( 'click', clearSelectedHotspot );

		// Handle accessibility open hotspot when focused and pressing enter.
		container.addEventListener( 'keydown', ( event ) => {
			if ( event.key === 'Enter' && event.target.classList.contains( 'interactive-storytelling__item' ) ) {
				handleHotspotClick( { currentTarget: event.target, target: event.target } );

				// Set focus to the flyout first focusable element.
				const focusableElements = flyout.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
				const firstFocusableElement = focusableElements[0];
				firstFocusableElement.focus();
			}
		} );


		container.addEventListener( 'click', ( event ) => {
			const target = event.target;

			// Handle clicking close inside of hotspot.
			const closeEl = target.classList.contains( 'interactive-storytelling__item-close' ) ? target : target.closest( '.interactive-storytelling__item-close' );

			if ( closeEl ) {
				clearSelectedHotspot();
				return;
			}

			const backEl = target.classList.contains( 'interactive-storytelling__flyout-back' ) ? target : target.closest( '.interactive-storytelling__flyout-back' );

			if ( backEl ) {
				clearSelectedHotspotRelation();
				return;
			}

			// Handle hotspot clicks
			const hotspotEl = target.classList.contains( 'interactive-storytelling__item' ) ? target : target.closest('.interactive-storytelling__item');

			if ( hotspotEl ) {
				handleHotspotClick( { currentTarget: hotspotEl, target } );
				return;
			}

			// Handle related hotspot clicks within the flyout
			const relationshipEl = target.classList.contains( 'interactive-storytelling__flyout-relationship' ) ? target : target.closest('.interactive-storytelling__flyout-relationship');

			if ( relationshipEl ) {
				handleHotspotRelationClick( { currentTarget: relationshipEl, target } );
				return;
			}
		} );

		// Hovering over a hotspot should highlight the related hotspots.
		container.addEventListener( 'mouseover', ( event ) => {
			const target = event.target;

			// Handle hotspot hover
			const hotspotEl = target.closest( '.interactive-storytelling__item' );

			if ( hotspotEl && null === selectedHotspot ) {
				highlightRelatedHotspots( hotspots[ hotspotEl.getAttribute( 'data-uid' ) ] );
				return;
			}
		} );

		// Clear the highlighted related hotspots when the mouse leaves the container.
		container.addEventListener( 'mouseout', ( event ) => {
			const target = event.target;

			// Handle hotspot hover out
			const hotspotEl = target.closest( '.interactive-storytelling__item' );

			if ( hotspotEl && null === selectedHotspot ) {
				clearHighlightedRelatedHotspots();
				return;
			}
		} );

		// Listen for escape key to close the flyout.
		document.addEventListener( 'keydown', ( event ) => {
			if ( event.key === 'Escape' ) {
				clearSelectedHotspot();
			}
		} );

		const resizeObserver = new ResizeObserver( entries => {
			for ( let entry of entries ) {
				const event = new CustomEvent( 'resizing', { detail: { width: entry.contentRect.width, height: entry.contentRect.height } } );

				containerItems.dispatchEvent( event );
			}
		} );

		resizeObserver.observe( containerItems );
	}

    return {
		/**
		 * Initialize the interactive storytelling.
		 *
		 * @param {HTMLElement} c
		 */
        init: function( c ) {
			if ( ! c ) {
				return;
			}

			container = c;

			setupHotspots();
			setupVariables();
			generateRelationshipLines();
			setupEvents();
        }
    };
} )();

// Let's begin the interactive storytelling.
document.addEventListener( 'DOMContentLoaded', function() {
	const c = document.querySelector( '.interactive-storytelling' );

	if ( ! ( c instanceof HTMLElement ) ) {
		return;
	}

    InteractiveStorytelling.init( c );
} );
